import { RefObject, useEffect, useState } from "react";
import { noop } from "../../utils";
import useRefDimensionsObserver from "../useRefDimensionsObserver";

interface IOptions {
    anchorRef: RefObject<HTMLElement>;
    dropdownRef: RefObject<HTMLElement>;
    isOpen: boolean;
    placement?: TDropdownPlacement;
}

export type TDropdownPosition = "left" | "right" | "auto";

// auto will render at the bottom by default and will render on top if not enough space at the bottom
export type TDropdownPlacement = "bottom" | "top" | "auto";

export interface IDropdownPagePosition {
    x: number;
    y: number;
    width: number;
}

export const useDropdownPosition = ({
    anchorRef,
    dropdownRef,
    isOpen,
    ...options
}: IOptions): IDropdownPagePosition => {
    const {
        dimensions: { width, height }
    } = useRefDimensionsObserver({ containerRef: anchorRef });
    const [position, setPosition] = useState<IDropdownPagePosition>({ x: 0, y: 0, width: 0 });

    const recalculatePosition = (anchorElement: HTMLElement, ddElement: HTMLElement) => {
        const anchorRect = anchorElement.getBoundingClientRect();
        const ddRect = ddElement.getBoundingClientRect();

        const placement = ((): TDropdownPlacement => {
            if (options.placement && options.placement !== "auto") {
                return options.placement;
            }
            // 16 is just a random value to give it some space below the screen
            return window.innerHeight > anchorRect.top + height + ddRect.height + 16 ? "bottom" : "top";
        })();

        const y = placement === "bottom" ? anchorRect.top + height + 8 : anchorRect.top - ddRect.height;
        const x = anchorRect.left;

        setPosition({ y, x, width });
    };

    useEffect(() => {
        const anchor = anchorRef.current;
        const dd = dropdownRef.current;

        if (!anchor || !dd || !isOpen) {
            return noop;
        }

        recalculatePosition(anchor, dd);

        const handleScroll = () => {
            window.requestAnimationFrame(() => recalculatePosition(anchor, dd));
        };

        window.addEventListener("scroll", handleScroll, { passive: true, capture: true });

        return () => {
            window.removeEventListener("scroll", handleScroll, { capture: true });
        };
    }, [anchorRef.current, dropdownRef.current, isOpen, width, height]);

    return position;
};
